"use client";

import { useEffect, useState } from "react";
import { useQuery } from "convex/react";
import { MapPin, Truck } from "lucide-react";
import { api } from "@convex/_generated/api";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCartStore } from "@/stores/cart-store";
import { useMarketingLocationContext } from "@/lib/marketing-location-context";
import { getPublicPageBrand } from "@/lib/public-brand-theme";
import { getDeliveryAddressDraftError } from "@/lib/ordering-state";

type DeliveryAddressSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirmed?: () => void;
};

type AddressDraft = {
  street: string;
  apt: string;
  city: string;
  state: string;
  zip: string;
  instructions: string;
};

const EMPTY_ADDRESS: AddressDraft = {
  street: "",
  apt: "",
  city: "Las Vegas",
  state: "NV",
  zip: "",
  instructions: "",
};

export function DeliveryAddressSheet({
  open,
  onOpenChange,
  onConfirmed,
}: DeliveryAddressSheetProps) {
  const deliveryAddress = useCartStore((state) => state.deliveryAddress);
  const setDeliveryAddress = useCartStore((state) => state.setDeliveryAddress);
  const setDeliveryZone = useCartStore((state) => state.setDeliveryZone);
  const clearUberDirectQuote = useCartStore(
    (state) => state.clearUberDirectQuote,
  );
  const setOrderType = useCartStore((state) => state.setOrderType);
  const { location, siteConfig } = useMarketingLocationContext();
  const pageBrand = getPublicPageBrand(siteConfig);
  const [draft, setDraft] = useState<AddressDraft>(EMPTY_ADDRESS);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDraft(
      deliveryAddress
        ? {
            street: deliveryAddress.street,
            apt: deliveryAddress.apt ?? "",
            city: deliveryAddress.city,
            state: deliveryAddress.state,
            zip: deliveryAddress.zip,
            instructions: deliveryAddress.instructions ?? "",
          }
        : EMPTY_ADDRESS,
    );
    setSubmitError(null);
  }, [deliveryAddress, open]);

  const zipDigits = draft.zip.replace(/\D/g, "").slice(0, 5);
  const matchingZone = useQuery(
    api.deliveryZones.findByZipCode,
    open && zipDigits.length === 5
      ? { zipCode: zipDigits, locationId: location.id }
      : "skip",
  );

  const updateDraft = (field: keyof AddressDraft, value: string) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setSubmitError(null);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const draftError = getDeliveryAddressDraftError({
      street: draft.street,
      city: draft.city,
      state: draft.state,
      zip: zipDigits,
    });
    if (draftError) {
      setSubmitError(draftError);
      return;
    }
    if (matchingZone === undefined) {
      setSubmitError("We’re still checking that delivery area.");
      return;
    }
    if (!matchingZone) {
      setSubmitError("Sorry, we don’t deliver to that ZIP code yet.");
      return;
    }

    clearUberDirectQuote();
    setDeliveryZone(matchingZone._id, 0);
    setDeliveryAddress({
      street: draft.street.trim(),
      apt: draft.apt.trim() || undefined,
      city: draft.city.trim(),
      state: draft.state.trim().toUpperCase(),
      zip: zipDigits,
      instructions: draft.instructions.trim() || undefined,
    });
    setOrderType("delivery");
    onOpenChange(false);
    onConfirmed?.();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-public-page-brand={pageBrand}
        data-order-light-surface="true"
        className="w-full overflow-y-auto bg-background p-0 sm:max-w-md"
      >
        <SheetHeader className="border-b border-border/50 px-5 py-5 text-left sm:px-6">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Truck className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <SheetTitle>Where should we deliver?</SheetTitle>
              <SheetDescription className="mt-1">
                Enter your address to confirm it’s in our delivery area.
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <form className="space-y-5 px-5 py-5 sm:px-6" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Label htmlFor="delivery-sheet-street">Street address</Label>
            <Input
              id="delivery-sheet-street"
              value={draft.street}
              onChange={(event) => updateDraft("street", event.target.value)}
              placeholder="123 Main St"
              autoComplete="street-address"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="delivery-sheet-apt">Apt / Suite (optional)</Label>
            <Input
              id="delivery-sheet-apt"
              value={draft.apt}
              onChange={(event) => updateDraft("apt", event.target.value)}
              placeholder="Unit 4"
              autoComplete="address-line2"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="delivery-sheet-city">City</Label>
              <Input
                id="delivery-sheet-city"
                value={draft.city}
                onChange={(event) => updateDraft("city", event.target.value)}
                autoComplete="address-level2"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="delivery-sheet-state">State</Label>
              <Input
                id="delivery-sheet-state"
                value={draft.state}
                onChange={(event) =>
                  updateDraft(
                    "state",
                    event.target.value.toUpperCase().slice(0, 2),
                  )
                }
                placeholder="NV"
                autoComplete="address-level1"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="delivery-sheet-zip">ZIP code</Label>
              <Input
                id="delivery-sheet-zip"
                value={draft.zip}
                onChange={(event) =>
                  updateDraft(
                    "zip",
                    event.target.value.replace(/\D/g, "").slice(0, 5),
                  )
                }
                placeholder="89141"
                autoComplete="postal-code"
                inputMode="numeric"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="delivery-sheet-instructions">
              Delivery instructions (optional)
            </Label>
            <Textarea
              id="delivery-sheet-instructions"
              value={draft.instructions}
              onChange={(event) =>
                updateDraft("instructions", event.target.value)
              }
              placeholder="Gate code, building name, or drop-off notes"
              className="min-h-24 resize-y"
            />
          </div>

          {zipDigits.length === 5 ? (
            <div
              role="status"
              className={
                matchingZone
                  ? "flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-900"
                  : "flex items-start gap-2 rounded-xl border border-border bg-muted/40 px-3 py-3 text-sm text-muted-foreground"
              }
            >
              <MapPin className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>
                {matchingZone === undefined
                   ? "Checking your delivery area…"
                   : matchingZone
                    ? "This address is in our delivery area."
                    : "Sorry, we don’t deliver to this ZIP code yet."}
              </span>
            </div>
          ) : null}

          {submitError ? (
            <p role="alert" className="text-sm font-medium text-destructive">
              {submitError}
            </p>
          ) : null}

          <Button
            type="submit"
            className="h-12 w-full rounded-xl text-base font-bold"
            disabled={!matchingZone}
          >
            Deliver to this address
          </Button>
          <p className="text-center text-xs leading-5 text-muted-foreground">
            Order minimum and courier availability are confirmed at checkout.
            The final Uber delivery fee is shown there too.
          </p>
        </form>
      </SheetContent>
    </Sheet>
  );
}
